/**
 * Detección del modo PREVIEW POR LINK (`?veoPreview=<token>`). El core solo
 * detecta el param — el canje del token y el render viven en el bundle de
 * guías (`plugins/guides/preview-mode.ts`), que el caller carga
 * (estático en `index.ts`, lazy en `veo.ts`).
 *
 * El nombre del query param es parte del contrato con el dashboard (el link
 * generado por «Previsualizar en mi app»).
 */
import { hasWindow } from '../utils/safe-env';

export const PREVIEW_TOKEN_PARAM = 'veoPreview';
/**
 * El token se PERSISTE en sessionStorage: al navegar entre páginas de la app la
 * URL pierde `?veoPreview`, y sin esto el modo preview (y su chip de control) se
 * apagaba al saltar de página. sessionStorage sobrevive la navegación de la
 * pestaña y muere al cerrarla — justo el alcance de una sesión de preview.
 */
const PREVIEW_TOKEN_KEY = 'veo:preview_token';

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null; // modo privado / sandbox: seguimos solo con la URL
  }
}

/**
 * Token del link de preview. Lo toma de la URL (`?veoPreview`) y lo persiste; si
 * la URL no lo trae (navegó a otra página), lo recupera de sessionStorage.
 * `null` si no hay ninguno. Se limpia con {@link clearPreviewToken}.
 */
export function getPreviewToken(): string | null {
  if (!hasWindow()) return null;
  let fromUrl: string | null = null;
  try {
    const value = new URLSearchParams(window.location.search).get(PREVIEW_TOKEN_PARAM);
    fromUrl = value ? value : null;
  } catch {
    fromUrl = null;
  }
  const store = session();
  if (fromUrl) {
    try {
      store?.setItem(PREVIEW_TOKEN_KEY, fromUrl);
    } catch {
      // no-op: sin storage seguimos con el token de la URL
    }
    return fromUrl;
  }
  try {
    return store?.getItem(PREVIEW_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/** Sale del modo preview: borra el token persistido (lo llama el chip al cerrar). */
export function clearPreviewToken(): void {
  try {
    session()?.removeItem(PREVIEW_TOKEN_KEY);
  } catch {
    // no-op
  }
}

/**
 * `true` cuando la app corre en modo preview-por-link. Igual que en modo
 * builder, el runtime NO debe instalar el plugin de guías: las guías live
 * taparían/duplicarían la guía que se está previsualizando.
 */
export function isPreviewMode(): boolean {
  return getPreviewToken() !== null;
}
